// frontend/src/components/ManageModels.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, setDoc, doc, deleteDoc, updateDoc, query, where } from 'firebase/firestore';

export default function ManageModels() {
    const [models, setModels] = useState([]);
    const [accounts, setAccounts] = useState([]);
    
    // Creation State
    const [newId, setNewId] = useState('');
    const [newDesc, setNewDesc] = useState('');
    
    // UI State
    const [loading, setLoading] = useState(false);
    const [expandedModel, setExpandedModel] = useState(null); // ID of expanded group
    const [editingModel, setEditingModel] = useState(null); // Object of model being edited (triggers Modal)

    // Fetch Data
    const fetchData = async () => {
        // Fetch Groups
        const mSnap = await getDocs(collection(db, "models"));
        const mList = [];
        mSnap.forEach(d => mList.push(d.data()));
        setModels(mList);

        // Fetch ALL Accounts (to map them under groups)
        const aSnap = await getDocs(collection(db, "accounts"));
        const aList = [];
        aSnap.forEach(d => aList.push({ id: d.id, ...d.data() }));
        setAccounts(aList);
    };

    useEffect(() => { fetchData(); }, [loading]);

    // --- ACTIONS ---
    const handleCreate = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await setDoc(doc(db, "models", newId), {
                modelId: newId,
                description: newDesc
            });
            setNewId('');
            setNewDesc('');
        } catch (error) {
            alert(error.message);
        }
        setLoading(false);
    };

    const handleSaveEdit = async () => {
        setLoading(true);
        try {
            await updateDoc(doc(db, "models", editingModel.modelId), {
                description: editingModel.description
            });
            setEditingModel(null); // Close Modal
        } catch (error) {
            alert(error.message);
        }
        setLoading(false);
    };

    const handleDelete = async () => {
        const linkedAccounts = accounts.filter(a => a.modelId === editingModel.modelId);
        if (linkedAccounts.length > 0) {
            alert(`Cannot delete: This group has ${linkedAccounts.length} active accounts linked to it. Delete them first.`);
            return;
        }
        if (!window.confirm("Delete this Group ID permanently?")) return;
        
        setLoading(true);
        await deleteDoc(doc(db, "models", editingModel.modelId));
        setEditingModel(null);
        setLoading(false);
    };

    return (
        <div className="admin-container">
            <h2>Manage Model Groups</h2>

            {/* Creation Bar */}
            <div style={{ background: '#252526', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
                <form onSubmit={handleCreate} style={{ display: 'flex', gap: '10px' }}>
                    <input placeholder="New Group ID (e.g. M1)" required value={newId} onChange={e => setNewId(e.target.value)} style={inputStyle} />
                    <input placeholder="Description (e.g. Agency Client A)" required value={newDesc} onChange={e => setNewDesc(e.target.value)} style={{...inputStyle, flex: 2}} />
                    <button disabled={loading} style={btnStyle}>Create Group</button>
                </form>
            </div>

            {/* List */}
            <div style={{ display: 'grid', gap: '10px' }}>
                {models.map(m => (
                    <div key={m.modelId} style={{ background: '#1e1e1e', border: '1px solid #444', borderRadius: '4px' }}>
                        {/* Header Row */}
                        <div style={{ padding: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', background: expandedModel === m.modelId ? '#333' : 'transparent' }} 
                             onClick={() => setExpandedModel(expandedModel === m.modelId ? null : m.modelId)}>
                            <div>
                                <strong style={{ color: '#007acc', fontSize: '1.1rem' }}>{m.modelId}</strong>
                                <span style={{ marginLeft: '10px', color: '#888' }}>{m.description}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                                <span style={{ fontSize: '0.8rem', color: '#666' }}>
                                    {expandedModel === m.modelId ? '▼' : '▶'} {accounts.filter(a => a.modelId === m.modelId).length} Accounts
                                </span>
                                <button 
                                    onClick={(e) => { e.stopPropagation(); setEditingModel(m); }}
                                    style={{ padding: '5px 10px', background: '#444', border: 'none', color: 'white', borderRadius: '3px', cursor: 'pointer' }}>
                                    Edit
                                </button>
                            </div>
                        </div>

                        {/* Expanded Accounts List */}
                        {expandedModel === m.modelId && (
                            <div style={{ padding: '10px 20px', background: '#111', borderTop: '1px solid #333' }}>
                                <h4 style={{ margin: '5px 0 10px', color: '#555' }}>Linked Accounts</h4>
                                {accounts.filter(a => a.modelId === m.modelId).length === 0 ? (
                                    <p style={{ fontStyle: 'italic', color: '#666' }}>No accounts yet.</p>
                                ) : (
                                    accounts.filter(a => a.modelId === m.modelId).map(acc => (
                                        <div key={acc.id} style={{ padding: '5px', borderBottom: '1px solid #222', display: 'flex', gap: '10px' }}>
                                            <span style={{ color: '#007acc' }}>{acc.name}</span>
                                            <span style={{ color: '#666' }}>({acc.tgHandle})</span>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* --- MODAL: EDIT GROUP --- */}
            {editingModel && (
                <div style={modalOverlayStyle}>
                    <div style={modalContentStyle}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
                            <h3>Edit Group: {editingModel.modelId}</h3>
                            <button onClick={() => setEditingModel(null)} style={closeBtnStyle}>✕</button>
                        </div>
                        
                        <label>Description</label>
                        <input 
                            value={editingModel.description} 
                            onChange={(e) => setEditingModel({...editingModel, description: e.target.value})}
                            style={{...inputStyle, width: '100%', marginBottom: '20px'}} 
                        />

                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <button onClick={handleDelete} style={{...btnStyle, background: '#a31515'}}>Delete Group</button>
                            <button onClick={handleSaveEdit} style={{...btnStyle, background: '#007acc'}}>Save Changes</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

// Common Styles
const inputStyle = { padding: '8px', background: '#3c3c3c', border: '1px solid #555', color: 'white' };
const btnStyle = { padding: '8px 15px', background: '#2da44e', color: 'white', border: 'none', cursor: 'pointer', borderRadius: '3px' };
const modalOverlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 };
const modalContentStyle = { background: '#252526', padding: '30px', borderRadius: '8px', width: '400px', border: '1px solid #444', boxShadow: '0 4px 15px rgba(0,0,0,0.5)' };
const closeBtnStyle = { background: 'transparent', border: 'none', color: '#888', fontSize: '1.2rem', cursor: 'pointer' };